import { useCallback, useMemo, useState } from 'react'
import { useApolloClient, useMutation } from '@apollo/client'
import { GET_ME } from 'api/auth/me'
import { SET_PRIMARY_WALLET } from 'api/wallets/set-primary-wallet'
import { UNLINK_WALLET } from 'api/wallets/unlink-wallet'
import {
  isWalletNotLinkedError,
  notifyPrimaryChanged,
  notifyWalletActionError,
  notifyWalletUnlinked
} from '../utils/notify'

export type WalletAction = 'primary' | 'unlink'

export interface PendingWalletAction {
  address: string
  action: WalletAction
}

const ACTIONS: Record<
  WalletAction,
  { done: (address: string) => void; failedTitle: string }
> = {
  primary: {
    done: notifyPrimaryChanged,
    failedTitle: 'Could not change the primary wallet'
  },
  unlink: {
    done: notifyWalletUnlinked,
    failedTitle: 'Could not unlink the wallet'
  }
}

/**
 * Makes a linked wallet primary or unlinks one (FE-305, backend PR #189). The
 * mutations answer with the user's wallets, so the cached `me` — and with it
 * `userVar`, the wallet lists and the wallet selected for play — updates by
 * itself. A wallet that left the account meanwhile (another tab, or unlinked
 * in Privy, which the backend mirrors by webhook) makes `me` refetch.
 */
export const useWalletActions = () => {
  const client = useApolloClient()
  const [setPrimaryWallet] = useMutation(SET_PRIMARY_WALLET)
  const [unlinkWallet] = useMutation(UNLINK_WALLET)
  const [pending, setPending] = useState<PendingWalletAction | null>(null)

  const perform = useCallback(
    ({ address, action }: PendingWalletAction) => {
      const variables = { walletAddress: address }

      return action === 'primary' ?
          setPrimaryWallet({ variables })
        : unlinkWallet({ variables })
    },
    [setPrimaryWallet, unlinkWallet]
  )

  const recover = useCallback(
    async (action: WalletAction, error: unknown) => {
      notifyWalletActionError(ACTIONS[action].failedTitle, error)
      if (!isWalletNotLinkedError(error)) return

      await client
        .query({ query: GET_ME, fetchPolicy: 'network-only' })
        .catch(() => undefined)
    },
    [client]
  )

  const run = useCallback(
    async (request: PendingWalletAction) => {
      setPending(request)
      try {
        await perform(request)
        ACTIONS[request.action].done(request.address)
      } catch (error) {
        await recover(request.action, error)
      } finally {
        setPending(null)
      }
    },
    [perform, recover]
  )

  const handlers = useMemo(
    () => ({
      makePrimary: (address: string) => run({ address, action: 'primary' }),
      unlink: (address: string) => run({ address, action: 'unlink' })
    }),
    [run]
  )

  return { pending, ...handlers }
}
